import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BookOpen, CreditCard, Minus, Plus, Tag, Truck, WhatsappLogo, Heart, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtPrecio } from "@/pages/LibreriaPage";
import { useFavoritos, compartir } from "@/utils/favoritos";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WA_NUMBER = "527223947826";
const ENVIO_MXN = 99;

const LibroPage = () => {
  const { slug } = useParams();
  const [book, setBook] = useState(null);
  const [todos, setTodos] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [cantidad, setCantidad] = useState(1);
  const [entrega, setEntrega] = useState("iglesia");
  const [form, setForm] = useState({ nombre: "", telefono: "", direccion: "" });
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");
  const [fotoIdx, setFotoIdx] = useState(0);
  const [favLibros, toggleFavLibro] = useFavoritos("libros");

  useEffect(() => {
    setBook(null);
    setNotFound(false);
    setCantidad(1);
    setFotoIdx(0);
    axios
      .get(`${API}/books/${slug}`)
      .then(({ data }) => {
        setBook(data);
        document.title = `${data.titulo} — Librería Viento de Gracia`;
      })
      .catch(() => setNotFound(true));
    axios.get(`${API}/books`).then(({ data }) => setTodos(data)).catch(() => {});
    window.scrollTo(0, 0);
  }, [slug]);

  const relacionados = useMemo(() => {
    if (!book || !todos.length) return [];
    const tags = new Set(book.tags || []);
    let rel = todos.filter((b) => b.id !== book.id && (b.tags || []).some((t) => tags.has(t)));
    if (rel.length < 4) {
      const ids = new Set(rel.map((b) => b.id));
      rel = [...rel, ...todos.filter((b) => b.id !== book.id && !ids.has(b.id) && b.destacado)];
    }
    if (rel.length < 4) {
      const ids = new Set(rel.map((b) => b.id));
      rel = [...rel, ...todos.filter((b) => b.id !== book.id && !ids.has(b.id))];
    }
    return rel.slice(0, 4);
  }, [book, todos]);

  // Datos estructurados para Google (rich results + Shopping)
  useEffect(() => {
    if (!book) return;
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Book",
      name: book.titulo,
      author: book.autor ? { "@type": "Person", name: book.autor } : undefined,
      isbn: book.isbn || undefined,
      image: `${window.location.origin}${book.portada}`,
      description: book.descripcion || `${book.titulo} — libro cristiano en Viento de Gracia, Toluca`,
      keywords: (book.tags || []).join(", "),
      offers: {
        "@type": "Offer",
        price: (book.precio_oferta > 0 && book.precio_oferta < book.precio) ? book.precio_oferta : book.precio,
        priceCurrency: "MXN",
        availability: book.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        itemCondition: "https://schema.org/NewCondition",
        url: window.location.href,
        seller: { "@type": "Organization", name: "Iglesia Viento de Gracia" },
      },
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "book-jsonld";
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);
    return () => document.getElementById("book-jsonld")?.remove();
  }, [book]);

  const total = useMemo(() => {
    if (!book) return 0;
    const precioFinal = (book.precio_oferta > 0 && book.precio_oferta < book.precio) ? book.precio_oferta : book.precio;
    return precioFinal * cantidad + (entrega === "domicilio" ? ENVIO_MXN : 0);
  }, [book, cantidad, entrega]);

  const comprar = async () => {
    setPayError("");
    if (!form.nombre.trim()) {
      setPayError("Escribe tu nombre para apartar el pedido");
      return;
    }
    if (entrega === "domicilio" && !form.direccion.trim()) {
      setPayError("Escribe la dirección de envío");
      return;
    }
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/libreria/checkout`, {
        book_id: book.id,
        cantidad,
        entrega,
        nombre: form.nombre,
        telefono: form.telefono,
        direccion: form.direccion,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const d = err.response?.data?.detail;
      setPayError(typeof d === "string" ? d : "No se pudo iniciar el pago, intenta de nuevo");
      setPaying(false);
    }
  };

  const waText = book
    ? encodeURIComponent(
        `Hola, quiero pedir el libro "${book.titulo}" (${cantidad} pza) de la librería Viento de Gracia. Entrega: ${entrega === "domicilio" ? "envío a domicilio" : "recojo en la iglesia"}. Mi nombre: ${form.nombre || "—"}`
      )
    : "";

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-ink px-6 text-center text-paper" data-testid="libro-notfound">
        <BookOpen size={40} weight="light" className="text-fire" />
        <p className="mt-6 font-serif text-3xl italic text-smoke">Este libro ya no está en el estante</p>
        <a href="/libreria" className="mt-8 border border-white/15 px-7 py-3 text-[10px] font-light uppercase tracking-[0.25em] text-paper/70 transition-colors hover:border-fire hover:text-fire" data-testid="libro-volver-catalogo">
          Ver todo el catálogo
        </a>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink text-paper" data-testid="libro-loading">
        <img src="/logo-mark-oficial.png" alt="Cargando" className="h-12 w-12 animate-float-slow object-contain" />
      </div>
    );
  }

  const agotado = book.stock <= 0;
  const inputCls =
    "w-full border-b border-white/15 bg-transparent py-3 text-sm font-light tracking-wide text-paper placeholder:text-paper/35 transition-colors duration-300 focus:border-fire focus:outline-none";

  return (
    <div className="min-h-screen bg-ink text-paper" data-testid="libro-page">
      <div className="grain-overlay" />

      <header className="flex items-center justify-between border-b border-white/10 px-6 py-5 lg:px-12">
        <a href="/" className="flex items-center gap-3" data-testid="libro-back-home">
          <img src="/logo-mark-oficial.png" alt="Viento de Gracia" className="h-9 w-9 object-contain" />
          <span className="font-serif text-sm uppercase tracking-[0.3em]">Viento de <em className="italic text-gold">Gracia</em></span>
        </a>
        <a href="/libreria" className="flex items-center gap-2 text-[10px] font-light uppercase tracking-[0.25em] text-paper/60 transition-colors hover:text-fire" data-testid="libro-back-libreria">
          <ArrowLeft size={14} weight="light" /> Librería
        </a>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-14 lg:px-12 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-[420px_1fr] lg:gap-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
            <div className="relative border border-white/10">
              <img
                src={`${BACKEND_URL}${(book.fotos?.[fotoIdx] || { url: book.portada }).url}`}
                alt={`${fotoIdx === 0 ? "Portada" : "Foto"} del libro ${book.titulo}`}
                className={`aspect-[3/4] w-full object-cover transition-opacity duration-300 ${agotado ? "opacity-40 grayscale" : ""}`}
                data-testid="libro-portada"
              />
              {book.destacado && (
                <span className="absolute left-0 top-4 bg-fire px-3 py-1 text-[9px] font-medium uppercase tracking-[0.2em] text-ink">Destacado</span>
              )}
            </div>
            {(book.fotos?.length || 0) > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2" data-testid="libro-fotos">
                {book.fotos.map((f, i) => (
                  <button
                    key={f.path}
                    onClick={() => setFotoIdx(i)}
                    data-testid={`libro-foto-${i}`}
                    className={`border transition-all duration-300 ${i === fotoIdx ? "border-fire" : "border-white/10 opacity-60 hover:opacity-100"}`}
                    aria-label={`Ver foto ${i + 1}`}
                  >
                    <img src={`${BACKEND_URL}${f.url}`} alt="" className="aspect-square w-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}>
            {book.tags?.length > 0 && (
              <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="libro-tags">
                <Tag size={13} weight="light" className="text-smoke" />
                {book.tags.map((t) => (
                  <a key={t} href={`/libreria?tag=${encodeURIComponent(t)}`} className="border border-white/15 px-3 py-1 text-[9px] font-light uppercase tracking-[0.2em] text-smoke transition-colors hover:border-fire hover:text-fire" data-testid={`libro-tag-${t}`}>
                    {t}
                  </a>
                ))}
              </div>
            )}
            <h1 className="font-serif text-4xl leading-tight lg:text-5xl" data-testid="libro-titulo">{book.titulo}</h1>
            {book.subtitulo && <p className="mt-2 font-serif text-xl italic text-paper/70" data-testid="libro-subtitulo">{book.subtitulo}</p>}
            {book.autor && <p className="mt-2 text-sm font-light uppercase tracking-[0.25em] text-smoke" data-testid="libro-autor">{book.autor}</p>}
            {book.precio_oferta > 0 && book.precio_oferta < book.precio ? (
              <div className="mt-6 flex flex-wrap items-center gap-4" data-testid="libro-precio">
                <span className="font-serif text-4xl text-fire">{fmtPrecio(book.precio_oferta)}</span>
                <span className="font-serif text-xl text-smoke line-through">{fmtPrecio(book.precio)}</span>
                <span className="bg-fire px-3 py-1 text-[9px] font-medium uppercase tracking-[0.2em] text-ink" data-testid="libro-oferta-badge">Oferta</span>
              </div>
            ) : (
              <p className="mt-6 font-serif text-4xl text-fire" data-testid="libro-precio">{fmtPrecio(book.precio)}</p>
            )}
            <p className="mt-1 text-[10px] font-light uppercase tracking-[0.25em] text-smoke" data-testid="libro-stock">
              {agotado ? "Agotado — vuelve pronto" : book.stock <= 3 ? `Últimas ${book.stock} piezas` : "Disponible"}
            </p>
            {book.descripcion && (
              <p className="mt-8 max-w-xl whitespace-pre-line text-sm font-light leading-relaxed tracking-wide text-paper/80" data-testid="libro-descripcion">
                {book.descripcion}
              </p>
            )}
            {book.resena && (
              <blockquote className="mt-8 max-w-xl border-l-2 border-fire pl-6" data-testid="libro-resena">
                <p className="text-[10px] font-light uppercase tracking-[0.3em] text-fire">Recomendación pastoral</p>
                <p className="mt-3 font-serif text-xl italic leading-relaxed text-paper/90">«{book.resena}»</p>
              </blockquote>
            )}
            {book.isbn && <p className="mt-4 text-[10px] font-light uppercase tracking-[0.25em] text-smoke/60" data-testid="libro-isbn">ISBN {book.isbn}</p>}

            {!agotado && (
              <div className="mt-10 border border-white/10 bg-surface p-7" data-testid="libro-compra">
                <div className="flex flex-wrap items-center gap-6">
                  <div>
                    <p className="mb-2 text-[10px] font-light uppercase tracking-[0.3em] text-smoke">Cantidad</p>
                    <div className="flex items-center border border-white/15">
                      <button onClick={() => setCantidad((c) => Math.max(1, c - 1))} data-testid="libro-qty-minus" className="flex h-11 w-11 items-center justify-center text-smoke transition-colors hover:text-fire" aria-label="Menos">
                        <Minus size={15} weight="light" />
                      </button>
                      <span className="w-10 text-center font-serif text-lg" data-testid="libro-qty">{cantidad}</span>
                      <button onClick={() => setCantidad((c) => Math.min(book.stock, 10, c + 1))} data-testid="libro-qty-plus" className="flex h-11 w-11 items-center justify-center text-smoke transition-colors hover:text-fire" aria-label="Más">
                        <Plus size={15} weight="light" />
                      </button>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="mb-2 text-[10px] font-light uppercase tracking-[0.3em] text-smoke">Entrega</p>
                    <div className="grid gap-px border border-white/15 bg-white/10 sm:grid-cols-2">
                      <button
                        onClick={() => setEntrega("iglesia")}
                        data-testid="libro-entrega-iglesia"
                        className={`flex items-center gap-2 px-4 py-3 text-left text-[10px] font-light uppercase tracking-[0.15em] transition-colors ${entrega === "iglesia" ? "bg-fire/10 text-fire" : "bg-surface text-smoke hover:text-paper"}`}
                      >
                        <BookOpen size={15} weight="light" /> Recojo en iglesia · Gratis
                      </button>
                      <button
                        onClick={() => setEntrega("domicilio")}
                        data-testid="libro-entrega-domicilio"
                        className={`flex items-center gap-2 px-4 py-3 text-left text-[10px] font-light uppercase tracking-[0.15em] transition-colors ${entrega === "domicilio" ? "bg-fire/10 text-fire" : "bg-surface text-smoke hover:text-paper"}`}
                      >
                        <Truck size={15} weight="light" /> Envío a domicilio · {fmtPrecio(ENVIO_MXN)}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <input value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} placeholder="Tu nombre *" className={inputCls} data-testid="libro-nombre" />
                  <input value={form.telefono} onChange={(e) => setForm((f) => ({ ...f, telefono: e.target.value }))} placeholder="Tu WhatsApp / teléfono" className={inputCls} data-testid="libro-telefono" />
                </div>
                {entrega === "domicilio" && (
                  <input value={form.direccion} onChange={(e) => setForm((f) => ({ ...f, direccion: e.target.value }))} placeholder="Dirección completa de envío (calle, número, colonia, CP) *" className={`${inputCls} mt-5`} data-testid="libro-direccion" />
                )}

                <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-5">
                  <span className="text-[10px] font-light uppercase tracking-[0.3em] text-smoke">Total</span>
                  <span className="font-serif text-3xl text-paper" data-testid="libro-total">{fmtPrecio(total)}</span>
                </div>

                {payError && <p className="mt-4 text-sm text-red-400" data-testid="libro-pay-error">{payError}</p>}

                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  <button
                    onClick={comprar}
                    disabled={paying}
                    data-testid="libro-comprar"
                    className="flex items-center justify-center gap-2 bg-fire px-6 py-4 text-[10px] font-medium uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-paper disabled:opacity-60"
                  >
                    <CreditCard size={15} weight="light" /> {paying ? "Abriendo pago..." : "Comprar con tarjeta"}
                  </button>
                  <a
                    href={`https://wa.me/${WA_NUMBER}?text=${waText}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="libro-whatsapp"
                    className="flex items-center justify-center gap-2 border border-white/15 px-6 py-4 text-[10px] font-light uppercase tracking-[0.25em] text-paper/80 transition-colors duration-300 hover:border-fire hover:text-fire"
                  >
                    <WhatsappLogo size={15} weight="light" /> Pedir por WhatsApp
                  </a>
                </div>
                <p className="mt-4 text-center text-[10px] font-light tracking-wide text-smoke/70">
                  Pago seguro con Stripe · Cada compra apoya los ministerios de la iglesia
                </p>
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="libro-acciones">
              <button
                onClick={() => {
                  const activo = toggleFavLibro(book.id);
                  toast.success(activo ? `"${book.titulo}" guardado en favoritos` : "Quitado de favoritos");
                }}
                data-testid="libro-fav-btn"
                className={`flex items-center gap-2 border px-5 py-2.5 text-[10px] font-light uppercase tracking-[0.2em] transition-colors duration-300 ${favLibros.includes(book.id) ? "border-fire bg-fire/10 text-fire" : "border-white/15 text-paper/70 hover:border-fire hover:text-fire"}`}
              >
                <Heart size={14} weight={favLibros.includes(book.id) ? "fill" : "light"} />
                {favLibros.includes(book.id) ? "En favoritos" : "Guardar"}
              </button>
              <button
                onClick={async () => {
                  const r = await compartir({
                    titulo: `${book.titulo} — Librería Viento de Gracia`,
                    texto: `Mira este libro: "${book.titulo}"${book.autor ? ` de ${book.autor}` : ""} — ${fmtPrecio((book.precio_oferta > 0 && book.precio_oferta < book.precio) ? book.precio_oferta : book.precio)} MXN en la librería Viento de Gracia`,
                    url: window.location.href,
                  });
                  if (r === "copied") toast.success("Enlace del libro copiado");
                }}
                data-testid="libro-share-btn"
                className="flex items-center gap-2 border border-white/15 px-5 py-2.5 text-[10px] font-light uppercase tracking-[0.2em] text-paper/70 transition-colors duration-300 hover:border-fire hover:text-fire"
              >
                <ShareNetwork size={14} weight="light" /> Compartir
              </button>
              <a
              href={`https://wa.me/?text=${encodeURIComponent(`Mira este libro de la librería Viento de Gracia: "${book.titulo}" ${window.location.href}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="libro-compartir"
              className="flex items-center gap-2 border border-white/15 px-5 py-2.5 text-[10px] font-light uppercase tracking-[0.2em] text-paper/70 transition-colors duration-300 hover:border-fire hover:text-fire"
            >
              <WhatsappLogo size={14} weight="light" /> WhatsApp
            </a>
            </div>
          </motion.div>
        </div>

        {relacionados.length > 0 && (
          <section className="mt-20 border-t border-white/10 pt-14" data-testid="libro-relacionados">
            <p className="text-[10px] font-light uppercase tracking-[0.4em] text-fire">Sigue edificándote</p>
            <h2 className="mt-4 font-serif text-3xl lg:text-4xl">También te puede <em className="italic text-gold">interesar</em></h2>
            <div className="mt-10 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
              {relacionados.map((r) => (
                <a key={r.id} href={`/libreria/${r.slug}`} className="group bg-ink" data-testid={`relacionado-${r.slug}`}>
                  <div className="overflow-hidden">
                    <img src={`${BACKEND_URL}${r.portada}`} alt={`Portada del libro ${r.titulo}`} className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <p className="font-serif text-lg leading-snug text-paper transition-colors group-hover:text-fire">{r.titulo}</p>
                    <div className="mt-2 flex items-center gap-3">
                      {r.precio_oferta > 0 && r.precio_oferta < r.precio ? (
                        <>
                          <span className="font-serif text-fire">{fmtPrecio(r.precio_oferta)}</span>
                          <span className="text-sm font-light text-smoke line-through">{fmtPrecio(r.precio)}</span>
                        </>
                      ) : (
                        <span className="font-serif text-fire">{fmtPrecio(r.precio)}</span>
                      )}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default LibroPage;
